import { useCallback, useEffect, useRef, useState } from 'react';
import { notifiche } from '../../api/notifiche';
import type { AvvisiMiei, NotificaLetta } from '../../api/notifiche';
import { Icona } from '../../components/Icona';

/*  LA CAMPANELLA, 16/10/2026. Gli avvisi chiesti da Galizzi — la richiesta d'acquisto
    arrivata all'ufficio, la merce arrivata per la commessa, la commessa da fatturare —
    stanno qui, in alto, dove prima c'era solo il nome di chi e' collegato. Niente mail.

    Si rilegge ogni minuto e quando si torna sulla finestra: un avviso che arriva mentre
    si compila un preventivo deve vedersi senza ricaricare la pagina, ma un minuto di
    ritardo non cambia niente a nessuno.

    Aprire un avviso lo segna letto e porta al documento.  */
const OGNI = 60_000;

export function Campanella() {
  const [avvisi, setAvvisi] = useState<AvvisiMiei | null>(null);
  const [aperta, setAperta] = useState(false);
  const riquadro = useRef<HTMLDivElement>(null);

  const rileggi = useCallback(() => {
    notifiche
      .mie()
      .then(setAvvisi)
      .catch(() => {
        // Una campanella che non risponde non deve diventare un errore a schermo: si
        // riprova al giro dopo.
      });
  }, []);

  useEffect(() => {
    rileggi();
    const giro = window.setInterval(rileggi, OGNI);
    window.addEventListener('focus', rileggi);
    return () => {
      window.clearInterval(giro);
      window.removeEventListener('focus', rileggi);
    };
  }, [rileggi]);

  useEffect(() => {
    if (!aperta) return;
    const fuori = (e: MouseEvent) => {
      if (!riquadro.current?.contains(e.target as Node)) setAperta(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAperta(false);
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperta]);

  async function apri(n: NotificaLetta) {
    setAperta(false);
    if (!n.letta) {
      await notifiche.letta(n.id).catch(() => undefined);
      rileggi();
    }
    if (n.collegamento) window.location.hash = `#${n.collegamento}`;
  }

  async function tutteLette() {
    await notifiche.tutteLette().catch(() => undefined);
    rileggi();
  }

  const nonLette = avvisi?.nonLette ?? 0;

  return (
    <div className="campanella" ref={riquadro}>
      <button
        type="button"
        className="campanella-bottone"
        aria-label={nonLette === 0 ? 'Avvisi' : `Avvisi: ${nonLette} da leggere`}
        aria-expanded={aperta}
        title="Avvisi"
        onClick={() => setAperta((a) => !a)}
      >
        <Icona nome="campanella" />
        {nonLette > 0 && <em className="coda">{nonLette > 99 ? '99+' : nonLette}</em>}
      </button>

      {aperta && (
        <div className="campanella-elenco" role="dialog" aria-label="Avvisi">
          <header>
            <strong>Avvisi</strong>
            {nonLette > 0 && (
              <button type="button" className="collegamento" onClick={() => void tutteLette()}>
                segna tutti letti
              </button>
            )}
          </header>
          {avvisi === null || avvisi.voci.length === 0 ? (
            <p className="nota">Nessun avviso.</p>
          ) : (
            <ul>
              {avvisi.voci.map((n) => (
                <li key={n.id} className={n.letta ? 'letta' : 'nuova'}>
                  <button type="button" onClick={() => void apri(n)}>
                    <span className="testo">{n.testo}</span>
                    <span className="quando">{quando(n.creataIl)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** «oggi 14:02», «ieri 9:15», o la data: un avviso si legge per quanto e' fresco. */
function quando(iso: string): string {
  const d = new Date(iso);
  const oggi = new Date();
  const ieri = new Date();
  ieri.setDate(oggi.getDate() - 1);
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  if (d.toDateString() === oggi.toDateString()) return `oggi ${ora}`;
  if (d.toDateString() === ieri.toDateString()) return `ieri ${ora}`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) + ` ${ora}`;
}
